/**
 * EnergyMeter.ts → ChallengeCompass
 * 4-axis radar SVG showing dungeon challenge distribution across
 * World's attribute axes: Physical, Mental, Social, Creative.
 *
 * Total area = energy budget. Content placement distorts the diamond.
 * Replaces the old chip-stack design with axis-aware challenge profiling.
 */

import type { GridCell, OctileCell, SquareConnector, CellType } from '../types/OctileTypes.ts'
import { SPAWN_ENERGY_COSTS, TRAP_ENERGY_COSTS } from '../types/ContentTypes.ts'

// ─── Axis System (matches World's 4-axis attribute model) ────

export type ChallengeAxis = 'physical' | 'mental' | 'social' | 'creative'

const AXIS_COLORS: Record<ChallengeAxis, string> = {
    physical: '#ff6666',
    mental:   '#66bbff',
    social:   '#ffcc44',
    creative: '#cc77ff',
}

const AXIS_LABELS: Record<ChallengeAxis, string> = {
    physical: 'Physical',
    mental:   'Mental',
    social:   'Social',
    creative: 'Creative',
}

const AXIS_ABBREV: Record<ChallengeAxis, string> = {
    physical: 'PHY',
    mental:   'MNT',
    social:   'SOC',
    creative: 'CRE',
}

// Axis attributes from World (for strength labeling)
const AXIS_ATTRS: Record<ChallengeAxis, string[]> = {
    physical: ['STR', 'AGI', 'END'],
    mental:   ['MAG', 'WIS', 'CUN'],
    social:   ['LDR', 'FTH', 'CHA'],
    creative: ['CRE', 'ART', 'INN'],
}

// Axes in render order: top, right, bottom, left
const AXES: ChallengeAxis[] = ['physical', 'mental', 'social', 'creative']

// ─── Content → Axis Cost Mapping ─────────────────────────────

// Cell types that imply challenge axes even without content
const CELL_TYPE_AXIS: Partial<Record<CellType, Partial<Record<ChallengeAxis, number>>>> = {
    // Combat cells → Physical
    guard_post:    { physical: 10 },
    patrol_route:  { physical: 8 },
    ambush:        { physical: 12 },
    nest:          { physical: 10 },
    arena:         { physical: 20 },
    barricade:     { physical: 8 },
    gauntlet:      { physical: 15, mental: 5 },
    boss_throne:   { physical: 25 },

    // Trap/puzzle cells → Mental
    trap_floor:    { mental: 10 },
    trap_walls:    { mental: 10 },
    trap_ceiling:  { mental: 10 },
    puzzle:        { mental: 15, creative: 10 },
    mimic_den:     { mental: 12 },

    // Social cells → Social
    shrine:        { social: 10 },
    entrance:      { social: 5 },
    library:       { social: 10, mental: 5 },
    prison:        { social: 8 },

    // Creative/exploration cells → Creative
    secret:        { creative: 15 },
    cache:         { creative: 10 },
    alcove:        { creative: 5 },
    crystal_cave:  { creative: 8 },
    mushroom_grotto: { creative: 8 },

    // Hazard cells → Physical
    lava_cell:     { physical: 10, mental: 5 },
    ice_cave:      { physical: 8 },
    collapsed:     { physical: 5 },
}

// Default per-item costs
const DEFAULT_SPAWN_COST = SPAWN_ENERGY_COSTS.minion   // 15
const DEFAULT_TRAP_COST = TRAP_ENERGY_COSTS.simple      // 15
const DEFAULT_LOOT_COST = 15
const DEFAULT_DECOR_COST = 5

// ─── SVG Constants ───────────────────────────────────────────

const SVG_SIZE = 200
const CENTER = SVG_SIZE / 2
const MAX_RADIUS = 72     // Max spoke length
const MIN_RADIUS = 10     // Min visible spoke
const RING_RADII = [24, 48, 72]  // Grid rings
const LABEL_OFFSET = 16

// Difficulty tier thresholds
const TIERS = [
    { max: 200,  label: 'Novice',       color: '#00ff88' },
    { max: 400,  label: 'Adventurer',   color: '#66bbff' },
    { max: 600,  label: 'Veteran',      color: '#ffcc44' },
    { max: 800,  label: 'Expert',       color: '#ff8844' },
    { max: 1000, label: 'Legendary',    color: '#ff4444' },
    { max: Infinity, label: 'IMPOSSIBLE', color: '#ff0000' },
]

// ─── Axis Profile ────────────────────────────────────────────

export interface AxisProfile {
    physical: number
    mental: number
    social: number
    creative: number
}

export class EnergyMeter {
    private root: HTMLElement
    private svgEl: SVGSVGElement
    private polygonEl: SVGPolygonElement
    private glowPolygonEl: SVGPolygonElement
    private spokeEls: SVGLineElement[] = []
    private dotEls: SVGCircleElement[] = []
    private axisLabelEls: SVGTextElement[] = []
    private axisValueEls: SVGTextElement[] = []
    private totalLabel: HTMLElement
    private tierLabel: HTMLElement
    private strengthsLabel: HTMLElement
    private resonanceLabel: HTMLElement
    private barFills: Record<ChallengeAxis, HTMLElement> = {} as any
    private barValues: Record<ChallengeAxis, HTMLElement> = {} as any
    private max: number
    private profile: AxisProfile = { physical: 0, mental: 0, social: 0, creative: 0 }
    private animFrame = 0
    private breathPhase = 0

    constructor(container: HTMLElement, maxEnergy = 1000) {
        this.max = maxEnergy
        this.root = this.buildDOM()
        this.svgEl = this.root.querySelector('.compass-svg')!
        this.polygonEl = this.svgEl.querySelector('.compass-polygon')!
        this.glowPolygonEl = this.svgEl.querySelector('.compass-glow')!
        this.totalLabel = this.root.querySelector('.compass-total')!
        this.tierLabel = this.root.querySelector('.compass-tier')!
        this.strengthsLabel = this.root.querySelector('.compass-strengths')!
        this.resonanceLabel = this.root.querySelector('.compass-resonance')!

        AXES.forEach((axis, i) => {
            this.spokeEls.push(this.svgEl.querySelectorAll('.compass-spoke')[i] as SVGLineElement)
            this.dotEls.push(this.svgEl.querySelectorAll('.compass-dot')[i] as SVGCircleElement)
            this.axisLabelEls.push(this.svgEl.querySelectorAll('.compass-axis-label')[i] as SVGTextElement)
            this.axisValueEls.push(this.svgEl.querySelectorAll('.compass-axis-value')[i] as SVGTextElement)
            this.barFills[axis] = this.root.querySelector(`.axis-bar-fill[data-axis="${axis}"]`)!
            this.barValues[axis] = this.root.querySelector(`.axis-bar-value[data-axis="${axis}"]`)!
        })

        container.appendChild(this.root)
        this.updateVisual()
        this.startBreathing()
    }

    // ─── Public API ──────────────────────────────────────

    addEnergy(delta: number): void {
        // Distribute evenly across axes for generic adds
        const perAxis = delta / 4
        this.profile.physical += perAxis
        this.profile.mental += perAxis
        this.profile.social += perAxis
        this.profile.creative += perAxis
        this.clampProfile()
        this.updateVisual()
    }

    setEnergy(current: number): void {
        // Legacy compat — distribute evenly
        const perAxis = Math.max(0, current) / 4
        this.profile = { physical: perAxis, mental: perAxis, social: perAxis, creative: perAxis }
        this.updateVisual()
    }

    setMaxEnergy(max: number): void {
        this.max = Math.max(1, max)
        this.updateVisual()
    }

    getCurrent(): number {
        return this.profile.physical + this.profile.mental +
               this.profile.social + this.profile.creative
    }

    getMax(): number { return this.max }
    getRatio(): number { return this.max > 0 ? this.getCurrent() / this.max : 0 }
    getProfile(): AxisProfile { return { ...this.profile } }

    recalculateFromGrid(grid: GridCell[][]): void {
        this.profile = { physical: 0, mental: 0, social: 0, creative: 0 }

        for (const row of grid) {
            if (!row) continue
            for (const cell of row) {
                if (!cell?.active) continue
                this.addCellToProfile(cell)
            }
        }

        this.updateVisual()
    }

    // ─── Profile Calculation ─────────────────────────────

    private addCellToProfile(cell: GridCell): void {
        if (!cell) return

        if (cell.kind === 'octagon') {
            const oct = cell as OctileCell

            // Cell type inherent challenge
            const typeAxes = CELL_TYPE_AXIS[oct.type]
            if (typeAxes) {
                for (const axis of AXES) {
                    if (typeAxes[axis]) this.profile[axis] += typeAxes[axis]!
                }
            }

            // Spawns → Physical
            this.profile.physical += oct.spawns.length * DEFAULT_SPAWN_COST

            // Traps → Mental
            this.profile.mental += oct.traps.length * DEFAULT_TRAP_COST

            // Loot → distributed (reward complexity)
            const lootCost = oct.loot.length * DEFAULT_LOOT_COST
            this.profile.creative += lootCost * 0.5
            this.profile.social += lootCost * 0.5

            // Decorations → Creative (ambiance)
            this.profile.creative += oct.decorations.length * DEFAULT_DECOR_COST

        } else if (cell.kind === 'connector') {
            const conn = cell as SquareConnector

            // Corridor traps → Mental
            this.profile.mental += conn.traps.length * DEFAULT_TRAP_COST

            // Corridor decor → Creative
            this.profile.creative += conn.decorations.length * DEFAULT_DECOR_COST
        }
    }

    private clampProfile(): void {
        for (const axis of AXES) {
            this.profile[axis] = Math.max(0, this.profile[axis])
        }
    }

    // ─── Visual Update ───────────────────────────────────

    private updateVisual(): void {
        const total = this.getCurrent()
        const ratio = this.getRatio()
        const overBudget = ratio > 1.0

        // Calculate spoke endpoints
        const points: string[] = []
        AXES.forEach((axis, i) => {
            const value = this.profile[axis]
            const maxPerAxis = this.max / 4
            const r = maxPerAxis > 0
                ? MIN_RADIUS + (value / maxPerAxis) * (MAX_RADIUS - MIN_RADIUS)
                : MIN_RADIUS
            const clampedR = Math.min(r, MAX_RADIUS + 4) // Allow slight overshoot
            const angle = (i * Math.PI / 2) - Math.PI / 2 // Start at top, go clockwise
            const x = CENTER + clampedR * Math.cos(angle)
            const y = CENTER + clampedR * Math.sin(angle)

            points.push(`${x.toFixed(1)},${y.toFixed(1)}`)

            // Update spoke line
            this.spokeEls[i]?.setAttribute('x2', x.toFixed(1))
            this.spokeEls[i]?.setAttribute('y2', y.toFixed(1))

            // Update dot
            this.dotEls[i]?.setAttribute('cx', x.toFixed(1))
            this.dotEls[i]?.setAttribute('cy', y.toFixed(1))
            this.dotEls[i]?.setAttribute('fill', AXIS_COLORS[axis])

            // Update axis value text
            if (this.axisValueEls[i]) {
                this.axisValueEls[i].textContent = String(Math.round(value))
            }
        })

        const pointsStr = points.join(' ')
        this.polygonEl.setAttribute('points', pointsStr)
        this.glowPolygonEl.setAttribute('points', pointsStr)

        // Color polygon based on budget state
        if (overBudget) {
            this.polygonEl.setAttribute('fill', 'rgba(255, 68, 68, 0.25)')
            this.polygonEl.setAttribute('stroke', '#ff4444')
            this.glowPolygonEl.setAttribute('stroke', '#ff4444')
            this.root.classList.add('over-budget')
        } else {
            this.polygonEl.setAttribute('fill', 'rgba(0, 255, 136, 0.12)')
            this.polygonEl.setAttribute('stroke', '#00ff88')
            this.glowPolygonEl.setAttribute('stroke', '#00ff88')
            this.root.classList.remove('over-budget')
        }

        // Labels
        this.totalLabel.textContent = `${Math.round(total)} / ${this.max} energy`

        // Tier
        const tier = TIERS.find(t => total <= t.max) || TIERS[TIERS.length - 1]
        this.tierLabel.textContent = tier.label
        this.tierLabel.style.color = tier.color

        // Top strengths
        const sorted = AXES
            .map(a => ({ axis: a, value: this.profile[a] }))
            .sort((a, b) => b.value - a.value)
            .filter(a => a.value > 0)

        if (sorted.length > 0) {
            const top = sorted.slice(0, 2)
            const strengthNames = this.getStrengthNames(top.map(t => t.axis))
            this.strengthsLabel.textContent = strengthNames.join(' · ')
        } else {
            this.strengthsLabel.textContent = 'No challenges placed'
        }

        // Resonance estimate (total / 10, scaled by balance)
        const balance = this.calculateBalance()
        const resonance = Math.round(total * balance * 0.1)
        this.resonanceLabel.textContent = resonance > 0
            ? `~${resonance} resonance yield`
            : ''

        // Update axis bars
        const maxPerAxis = this.max / 4
        for (const axis of AXES) {
            const pct = maxPerAxis > 0
                ? Math.min(100, (this.profile[axis] / maxPerAxis) * 100)
                : 0
            const fill = this.barFills[axis]
            const val = this.barValues[axis]
            if (fill) {
                fill.style.width = `${pct}%`
                fill.style.backgroundColor = pct > 100 ? '#ff4444' : AXIS_COLORS[axis]
            }
            if (val) {
                val.textContent = String(Math.round(this.profile[axis]))
            }
        }

        // Over-budget state
        if (overBudget) {
            this.totalLabel.style.color = '#ff4444'
            this.resonanceLabel.textContent = 'OVER BUDGET'
            this.resonanceLabel.style.color = '#ff4444'
        } else {
            this.totalLabel.style.color = ''
            this.resonanceLabel.style.color = ''
        }
    }

    private calculateBalance(): number {
        // How balanced are the 4 axes? 1.0 = perfect balance, 0.25 = all on one axis
        const total = this.getCurrent()
        if (total === 0) return 1
        const ideal = total / 4
        let deviation = 0
        for (const axis of AXES) {
            deviation += Math.abs(this.profile[axis] - ideal)
        }
        // Normalize: 0 deviation = 1.0 balance, max deviation = 0.5
        return Math.max(0.5, 1 - deviation / (total * 1.5))
    }

    private getStrengthNames(topAxes: ChallengeAxis[]): string[] {
        // Map axis combos to World's derived strength names
        const strengthMap: Record<string, string> = {
            'physical': 'Prowess',
            'mental': 'Arcane',
            'social': 'Diplomacy',
            'creative': 'Ingenuity',
            'physical+mental': 'Battlemage',
            'physical+social': 'Commander',
            'physical+creative': 'Tactician',
            'mental+social': 'Sage',
            'mental+creative': 'Artificer',
            'social+creative': 'Performer',
        }

        if (topAxes.length === 1) {
            return [strengthMap[topAxes[0]] || AXIS_LABELS[topAxes[0]]]
        }

        const key = topAxes.slice(0, 2).sort().join('+')
        const combo = strengthMap[key]
        if (combo) return [combo]

        return topAxes.slice(0, 2).map(a => strengthMap[a] || AXIS_LABELS[a])
    }

    // ─── Breathing Animation ─────────────────────────────

    private startBreathing(): void {
        const animate = () => {
            this.breathPhase += 0.02
            const scale = 1 + Math.sin(this.breathPhase) * 0.015
            this.glowPolygonEl?.setAttribute('transform',
                `translate(${CENTER},${CENTER}) scale(${scale.toFixed(4)}) translate(${-CENTER},${-CENTER})`)
            this.animFrame = requestAnimationFrame(animate)
        }
        this.animFrame = requestAnimationFrame(animate)
    }

    // ─── DOM Construction ────────────────────────────────

    private buildDOM(): HTMLElement {
        const root = document.createElement('div')
        root.className = 'challenge-compass'

        const ns = 'http://www.w3.org/2000/svg'
        const svg = document.createElementNS(ns, 'svg')
        svg.setAttribute('viewBox', `0 0 ${SVG_SIZE} ${SVG_SIZE}`)
        svg.setAttribute('class', 'compass-svg')

        // Defs: glow filter
        const defs = document.createElementNS(ns, 'defs')
        const filter = document.createElementNS(ns, 'filter')
        filter.setAttribute('id', 'compassGlow')
        filter.setAttribute('x', '-50%')
        filter.setAttribute('y', '-50%')
        filter.setAttribute('width', '200%')
        filter.setAttribute('height', '200%')
        const blur = document.createElementNS(ns, 'feGaussianBlur')
        blur.setAttribute('stdDeviation', '3')
        blur.setAttribute('result', 'blur')
        const merge = document.createElementNS(ns, 'feMerge')
        const m1 = document.createElementNS(ns, 'feMergeNode')
        m1.setAttribute('in', 'blur')
        const m2 = document.createElementNS(ns, 'feMergeNode')
        m2.setAttribute('in', 'SourceGraphic')
        merge.appendChild(m1)
        merge.appendChild(m2)
        filter.appendChild(blur)
        filter.appendChild(merge)
        defs.appendChild(filter)
        svg.appendChild(defs)

        // Grid rings
        for (const r of RING_RADII) {
            const ring = document.createElementNS(ns, 'circle')
            ring.setAttribute('cx', String(CENTER))
            ring.setAttribute('cy', String(CENTER))
            ring.setAttribute('r', String(r))
            ring.setAttribute('fill', 'none')
            ring.setAttribute('stroke', '#333')
            ring.setAttribute('stroke-width', '0.5')
            ring.setAttribute('stroke-dasharray', '2,3')
            svg.appendChild(ring)
        }

        // Grid cross-hairs (axis lines to max)
        for (let i = 0; i < 4; i++) {
            const angle = (i * Math.PI / 2) - Math.PI / 2
            const line = document.createElementNS(ns, 'line')
            line.setAttribute('x1', String(CENTER))
            line.setAttribute('y1', String(CENTER))
            line.setAttribute('x2', String(CENTER + MAX_RADIUS * Math.cos(angle)))
            line.setAttribute('y2', String(CENTER + MAX_RADIUS * Math.sin(angle)))
            line.setAttribute('stroke', '#2a2a2a')
            line.setAttribute('stroke-width', '0.5')
            svg.appendChild(line)
        }

        // Glow polygon (behind main, blurred)
        const glowPoly = document.createElementNS(ns, 'polygon')
        glowPoly.setAttribute('class', 'compass-glow')
        glowPoly.setAttribute('fill', 'none')
        glowPoly.setAttribute('stroke', '#00ff88')
        glowPoly.setAttribute('stroke-width', '2')
        glowPoly.setAttribute('filter', 'url(#compassGlow)')
        glowPoly.setAttribute('opacity', '0.4')
        glowPoly.setAttribute('points', `${CENTER},${CENTER} ${CENTER},${CENTER} ${CENTER},${CENTER} ${CENTER},${CENTER}`)
        svg.appendChild(glowPoly)

        // Main polygon
        const poly = document.createElementNS(ns, 'polygon')
        poly.setAttribute('class', 'compass-polygon')
        poly.setAttribute('fill', 'rgba(0, 255, 136, 0.12)')
        poly.setAttribute('stroke', '#00ff88')
        poly.setAttribute('stroke-width', '1.5')
        poly.setAttribute('stroke-linejoin', 'round')
        poly.setAttribute('points', `${CENTER},${CENTER} ${CENTER},${CENTER} ${CENTER},${CENTER} ${CENTER},${CENTER}`)
        svg.appendChild(poly)

        // Spokes + dots + labels for each axis
        AXES.forEach((axis, i) => {
            const angle = (i * Math.PI / 2) - Math.PI / 2

            // Spoke line
            const spoke = document.createElementNS(ns, 'line')
            spoke.setAttribute('class', 'compass-spoke')
            spoke.setAttribute('x1', String(CENTER))
            spoke.setAttribute('y1', String(CENTER))
            spoke.setAttribute('x2', String(CENTER))
            spoke.setAttribute('y2', String(CENTER))
            spoke.setAttribute('stroke', AXIS_COLORS[axis])
            spoke.setAttribute('stroke-width', '2')
            spoke.setAttribute('stroke-linecap', 'round')
            spoke.setAttribute('opacity', '0.7')
            svg.appendChild(spoke)

            // Dot at spoke end
            const dot = document.createElementNS(ns, 'circle')
            dot.setAttribute('class', 'compass-dot')
            dot.setAttribute('cx', String(CENTER))
            dot.setAttribute('cy', String(CENTER))
            dot.setAttribute('r', '4.5')
            dot.setAttribute('fill', AXIS_COLORS[axis])
            dot.setAttribute('stroke', '#000')
            dot.setAttribute('stroke-width', '0.5')
            svg.appendChild(dot)

            // Axis label (PHY, MNT, SOC, CRE) at edge
            const labelX = CENTER + (MAX_RADIUS + LABEL_OFFSET) * Math.cos(angle)
            const labelY = CENTER + (MAX_RADIUS + LABEL_OFFSET) * Math.sin(angle)
            const label = document.createElementNS(ns, 'text')
            label.setAttribute('class', 'compass-axis-label')
            label.setAttribute('x', String(labelX))
            label.setAttribute('y', String(labelY))
            label.setAttribute('text-anchor', 'middle')
            label.setAttribute('dominant-baseline', 'central')
            label.setAttribute('fill', AXIS_COLORS[axis])
            label.setAttribute('font-size', '11')
            label.setAttribute('font-family', "'Space Mono', monospace")
            label.setAttribute('font-weight', '600')
            label.textContent = AXIS_ABBREV[axis]
            svg.appendChild(label)

            // Value label (number) slightly outside
            const valX = CENTER + (MAX_RADIUS + LABEL_OFFSET + 12) * Math.cos(angle)
            const valY = CENTER + (MAX_RADIUS + LABEL_OFFSET + 12) * Math.sin(angle)
            const valLabel = document.createElementNS(ns, 'text')
            valLabel.setAttribute('class', 'compass-axis-value')
            valLabel.setAttribute('x', String(valX))
            valLabel.setAttribute('y', String(valY))
            valLabel.setAttribute('text-anchor', 'middle')
            valLabel.setAttribute('dominant-baseline', 'central')
            valLabel.setAttribute('fill', '#707070')
            valLabel.setAttribute('font-size', '9')
            valLabel.setAttribute('font-family', "'Space Mono', monospace")
            valLabel.textContent = '0'
            svg.appendChild(valLabel)
        })

        // Center dot
        const centerDot = document.createElementNS(ns, 'circle')
        centerDot.setAttribute('cx', String(CENTER))
        centerDot.setAttribute('cy', String(CENTER))
        centerDot.setAttribute('r', '2')
        centerDot.setAttribute('fill', '#00ff88')
        centerDot.setAttribute('opacity', '0.6')
        svg.appendChild(centerDot)

        // Build wrapper
        const header = document.createElement('div')
        header.className = 'compass-header'
        header.textContent = 'Challenge Compass'

        const total = document.createElement('div')
        total.className = 'compass-total'
        total.textContent = '0 / 1000 energy'

        const tier = document.createElement('div')
        tier.className = 'compass-tier'
        tier.textContent = 'Novice'

        const strengths = document.createElement('div')
        strengths.className = 'compass-strengths'
        strengths.textContent = 'No challenges placed'

        const resonance = document.createElement('div')
        resonance.className = 'compass-resonance'
        resonance.textContent = ''

        // Axis breakdown bars
        const barsContainer = document.createElement('div')
        barsContainer.className = 'compass-bars'

        for (const axis of AXES) {
            const row = document.createElement('div')
            row.className = 'axis-bar-row'

            const label = document.createElement('span')
            label.className = 'axis-bar-label'
            label.style.color = AXIS_COLORS[axis]
            label.textContent = AXIS_LABELS[axis]

            const track = document.createElement('div')
            track.className = 'axis-bar-track'

            const fill = document.createElement('div')
            fill.className = 'axis-bar-fill'
            fill.dataset.axis = axis
            fill.style.backgroundColor = AXIS_COLORS[axis]
            fill.style.width = '0%'
            track.appendChild(fill)

            const value = document.createElement('span')
            value.className = 'axis-bar-value'
            value.dataset.axis = axis
            value.textContent = '0'

            row.appendChild(label)
            row.appendChild(track)
            row.appendChild(value)
            barsContainer.appendChild(row)
        }

        root.appendChild(header)
        root.appendChild(svg)
        root.appendChild(barsContainer)
        root.appendChild(total)
        root.appendChild(tier)
        root.appendChild(strengths)
        root.appendChild(resonance)

        return root
    }
}
